import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Plus,
  Trash2,
  Save,
  CheckCircle,
  AlertCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = [
  { value: 0, label: "Monday", short: "Mon" },
  { value: 1, label: "Tuesday", short: "Tue" },
  { value: 2, label: "Wednesday", short: "Wed" },
  { value: 3, label: "Thursday", short: "Thu" },
  { value: 4, label: "Friday", short: "Fri" },
  { value: 5, label: "Saturday", short: "Sat" },
  { value: 6, label: "Sunday", short: "Sun" }
];

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
  { value: "America/Phoenix", label: "Arizona (no DST)" },
  { value: "UTC", label: "UTC" }
];

const AvailabilityScheduler = ({ user }) => {
  const navigate = useNavigate();
  const [blocks, setBlocks] = useState([]);
  const [timezone, setTimezone] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);

  useEffect(() => {
    loadSchedule();
    checkCurrentStatus();
  }, []);

  const loadSchedule = async () => {
    try {
      const response = await axios.get(`${API}/responder/schedule`);
      setBlocks(response.data.blocks || []);
      setTimezone(response.data.timezone || detectTimezone() || "");
    } catch (error) {
      console.error("Failed to load schedule:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkCurrentStatus = async () => {
    try {
      const response = await axios.get(`${API}/responder/schedule/check`);
      setCurrentStatus(response.data);
    } catch (error) {
      console.error("Failed to check status:", error);
    }
  };

  const detectTimezone = () => {
    try {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const supported = TIMEZONES.find(tz => tz.value === browserTimezone);
      if (supported) return browserTimezone;
      
      if (browserTimezone.includes("America/New_York") || browserTimezone.includes("US/Eastern")) {
        return "America/New_York";
      }
      if (browserTimezone.includes("America/Chicago") || browserTimezone.includes("US/Central")) {
        return "America/Chicago";
      }
      if (browserTimezone.includes("America/Denver") || browserTimezone.includes("US/Mountain")) {
        return "America/Denver";
      }
      if (browserTimezone.includes("America/Los_Angeles") || browserTimezone.includes("US/Pacific")) {
        return "America/Los_Angeles";
      }
      return null;
    } catch {
      return null;
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/responder/schedule`, {
        blocks: blocks,
        timezone: timezone
      });
      toast.success("Schedule saved! Your availability will update automatically.");
      checkCurrentStatus();
    } catch (error) {
      toast.error("Failed to save schedule");
    } finally {
      setIsSaving(false);
    }
  };

  const addBlock = () => {
    setBlocks([...blocks, { day: 0, start_time: "09:00", end_time: "17:00" }]);
  };

  const removeBlock = (index) => {
    setBlocks(blocks.filter((_, i) => i !== index));
  };

  const updateBlock = (index, field, value) => {
    const newBlocks = [...blocks];
    newBlocks[index] = { ...newBlocks[index], [field]: field === "day" ? parseInt(value) : value };
    setBlocks(newBlocks);
  };

  const getDayLabel = (dayNum) => {
    const day = DAYS.find(d => d.value === dayNum);
    return day ? day.label : "Unknown";
  };

  // Group blocks by day for visual display
  const blocksByDay = DAYS.map(day => ({
    ...day,
    blocks: blocks.filter(b => b.day === day.value).sort((a, b) => a.start_time.localeCompare(b.start_time))
  }));

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading schedule...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/profile")}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  Availability Schedule
                </h1>
                <p className="text-sm text-muted-foreground">
                  Set your weekly recurring hours
                </p>
              </div>
            </div>
            <Button onClick={handleSave} disabled={isSaving} data-testid="save-schedule-btn">
              {isSaving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Schedule
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Current Status */}
        {currentStatus?.scheduled && (
          <Card className={`card-valor mb-6 ${currentStatus.currently_available ? 'border-green-500 bg-green-50' : 'border-amber-500 bg-amber-50'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                {currentStatus.currently_available ? (
                  <>
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="font-semibold text-green-800">You're Currently Available</p>
                      <p className="text-sm text-green-700">
                        Until {currentStatus.current_block?.end_time} ({getDayLabel(currentStatus.local_day)})
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-6 h-6 text-amber-600" />
                    <div>
                      <p className="font-semibold text-amber-800">Currently Outside Scheduled Hours</p>
                      {currentStatus.next_block && (
                        <p className="text-sm text-amber-700">
                          Next: {getDayLabel(currentStatus.next_block.day)} at {currentStatus.next_block.start_time}
                          {currentStatus.next_block.days_until > 0 && ` (in ${currentStatus.next_block.days_until} day${currentStatus.next_block.days_until > 1 ? 's' : ''})`}
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Timezone Selection */}
        <Card className="card-valor mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Your Timezone
            </CardTitle>
            <CardDescription>
              Schedule times will be in this timezone
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger data-testid="schedule-timezone-select">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Weekly Overview */}
        <Card className="card-valor mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Weekly Schedule Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-2">
              {blocksByDay.map((day) => (
                <div key={day.value} className="text-center">
                  <p className="text-sm font-medium mb-2">{day.short}</p>
                  {day.blocks.length > 0 ? (
                    <div className="space-y-1">
                      {day.blocks.map((block, idx) => (
                        <div key={idx} className="bg-primary/10 text-primary text-xs rounded p-1">
                          {block.start_time.slice(0,5)}-{block.end_time.slice(0,5)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-muted text-muted-foreground text-xs rounded p-1">
                      Off
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Time Blocks Editor */}
        <Card className="card-valor">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Time Blocks</CardTitle>
              <CardDescription>
                Add time ranges when you're available to help veterans
              </CardDescription>
            </div>
            <Button variant="outline" onClick={addBlock} data-testid="add-block-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Block
            </Button>
          </CardHeader>
          <CardContent>
            {blocks.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No availability blocks set</p>
                <p className="text-sm">Click "Add Block" to set your available hours</p>
              </div>
            ) : (
              <div className="space-y-4">
                {blocks.map((block, index) => (
                  <div 
                    key={index} 
                    className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg"
                    data-testid={`block-${index}`}
                  >
                    <div className="flex-1 grid grid-cols-3 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs">Day</Label>
                        <Select 
                          value={block.day.toString()} 
                          onValueChange={(v) => updateBlock(index, "day", v)}
                        >
                          <SelectTrigger data-testid={`block-${index}-day`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DAYS.map((day) => (
                              <SelectItem key={day.value} value={day.value.toString()}>
                                {day.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Start Time</Label>
                        <Input
                          type="time"
                          value={block.start_time}
                          onChange={(e) => updateBlock(index, "start_time", e.target.value)}
                          data-testid={`block-${index}-start`}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">End Time</Label>
                        <Input
                          type="time"
                          value={block.end_time}
                          onChange={(e) => updateBlock(index, "end_time", e.target.value)}
                          data-testid={`block-${index}-end`}
                        />
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeBlock(index)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      data-testid={`block-${index}-remove`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Text */}
        <Card className="card-valor mt-6 bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold">How Scheduling Works</p>
                <ul className="mt-2 space-y-1 text-blue-700">
                  <li>• Your "Available" status will automatically update based on this schedule</li>
                  <li>• You can still manually toggle availability on your dashboard</li>
                  <li>• Veterans will only be matched with available responders</li>
                  <li>• Multiple time blocks per day are supported</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AvailabilityScheduler;
